import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import SellerLayout from "@/components/layouts/SellerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Eye,
  Users,
  ShoppingCart,
  IndianRupee,
  TrendingUp,
  Package,
} from "lucide-react";
import { toast } from "sonner";

const RANGE_LABEL = {
  daily: "Last 24h",
  weekly: "Last 7 days",
  monthly: "Last 30 days",
  yearly: "Last 12 months",
};

const SellerAnalytics = () => {
  const { getAuthHeaders } = useAuth();
  const [range, setRange] = useState("weekly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/seller/analytics`, {
        headers: getAuthHeaders(),
        params: { range },
      });
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load store analytics");
    }
    setLoading(false);
  }, [range, getAuthHeaders]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const Kpi = ({ label, value, Icon, tone }) => (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${tone}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <p
              className="text-2xl font-bold"
              data-testid={`seller-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {value}
            </p>
            <p className="text-sm text-gray-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <SellerLayout title="Store Analytics">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <p className="text-gray-500">
          Visits, top products, and conversion for {RANGE_LABEL[range]}.
        </p>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-40" data-testid="seller-analytics-range-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Last 24h</SelectItem>
            <SelectItem value="weekly">Last 7 days</SelectItem>
            <SelectItem value="monthly">Last 30 days</SelectItem>
            <SelectItem value="yearly">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : data ? (
        <>
          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Kpi label="Total Visits" value={data.total_visits} Icon={Eye} tone="bg-blue-100 text-blue-600" />
            <Kpi
              label="Unique Visitors"
              value={data.unique_visitors}
              Icon={Users}
              tone="bg-purple-100 text-purple-600"
            />
            <Kpi
              label="Confirmed Orders"
              value={data.confirmed_conversions}
              Icon={ShoppingCart}
              tone="bg-fuchsia-100 text-fuchsia-600"
            />
            <Kpi
              label="Conversion Rate"
              value={`${data.conversion_rate}%`}
              Icon={TrendingUp}
              tone="bg-amber-100 text-amber-600"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-3 rounded-xl bg-secondary-100 text-secondary-600">
                  <IndianRupee className="w-5 h-5" />
                </div>
                <div>
                  <p
                    className="text-xl font-bold"
                    data-testid="seller-kpi-revenue"
                  >
                    ₹{Number(data.revenue || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">Revenue from confirmed orders</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
                  <Package className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xl font-bold">{data.total_products}</p>
                  <p className="text-xs text-gray-500">Live products</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-3 rounded-xl bg-yellow-100 text-yellow-600">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xl font-bold">{data.conversions}</p>
                  <p className="text-xs text-gray-500">Total orders (any status)</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trend + Top Products */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-0 shadow-sm lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary-500" />
                  Visits Over Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {data.clicks_by_day?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.clicks_by_day}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="#0f766e"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-gray-400">
                      No visit data yet — share your store link to start!
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Top Products</CardTitle>
              </CardHeader>
              <CardContent>
                {data.top_products?.length > 0 ? (
                  <div className="space-y-3">
                    {data.top_products.map((p, i) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
                      >
                        <span className="w-6 text-xs text-gray-400">#{i + 1}</span>
                        <div className="w-10 h-10 rounded bg-gray-100 overflow-hidden flex-shrink-0">
                          {p.image ? (
                            <img
                              src={p.image}
                              alt={p.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-4 h-4 text-gray-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.name}</p>
                          {p.price != null && (
                            <p className="text-xs text-gray-400">₹{p.price}</p>
                          )}
                        </div>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                          {p.clicks} clicks
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">
                    No product clicks yet
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Eye className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Analytics Data
            </h3>
            <p className="text-gray-500">
              Share your store link and add products to see analytics here.
            </p>
          </CardContent>
        </Card>
      )}
    </SellerLayout>
  );
};

export default SellerAnalytics;
